import webApi from "./api"
import { logError } from "@/helper/utils"

export class PhotoUploadService {
  async uploadImage(formData, id, onProgressCallback = null) {
    try {
      // Mark upload start time for iOS refresh detection
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        const uploadStartTime = Date.now();
        sessionStorage.setItem(`upload_start_${id}`, uploadStartTime.toString());
      }

      const { data } = await webApi.post('/car-detail/image', formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        ...(onProgressCallback ? { onUploadProgress: onProgressCallback } : {}),
      });

      // Clear upload start marker on success
      if (isIOS) {
        sessionStorage.removeItem(`upload_start_${id}`);
      }
      
      return ({
        ...data,
        id,
        onUpload: false
      })
    } catch (e) {
      logError(`Upload image error: ${e}`);

      // Detect iOS cancellation
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isNetworkError = !e.response && (e.message?.toLowerCase().includes('network error') || e.message?.toLowerCase().includes('network') || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED');

      // Handle stuck upload issue
      if (isIOS && isNetworkError) {
        // Check if upload start marker exists (if not, page was refreshed)
        const uploadStartTime = sessionStorage.getItem(`upload_start_${id}`);

        // If marker doesn't exist, it means page was refreshed (sessionStorage cleared)
        if (!uploadStartTime) {
          return {
            id,
            onUpload: true,
            url: '',
            isUploadInterrupted: true,
          }
        }

        // Calculate upload duration
        const uploadDuration = Date.now() - parseInt(uploadStartTime, 10);

        // Check navigation type for reload
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        const isPageReload = navigationEntry && navigationEntry.type === 'reload';

        // Check if page was just loaded (indicating refresh during upload)
        const pageLoadTime = performance.timing ? performance.timing.navigationStart : null;
        const timeSincePageLoad = pageLoadTime ? Date.now() - pageLoadTime : null;
        const isRecentPageLoad = timeSincePageLoad !== null && timeSincePageLoad < 2000; // Page loaded less than 2 seconds ago

        // If it's a reload or (very quick failure AND recent page load), it's likely a refresh
        // Very quick failure (< 500ms) usually indicates refresh, not network disconnection
        const isVeryQuickFailure = uploadDuration < 500;

        if (isPageReload || (isVeryQuickFailure && isRecentPageLoad)) {
          sessionStorage.removeItem(`upload_start_${id}`);
          return {
            id,
            onUpload: true,
            url: '',
            isUploadInterrupted: true,
          }
        }

        // Otherwise, it's a network disconnection (upload was running for a while before failing)
        sessionStorage.removeItem(`upload_start_${id}`);
        return ({
          id,
          onUpload: false,
          err: true,
          url: ''
        })
      }

      // Clear upload start marker (iOS) for all other errors that are not handled
      if (isIOS) {
        sessionStorage.removeItem(`upload_start_${id}`);
      }

      return ({
        id,
        onUpload: false,
        err: true,
        url: ''
      })
    }
  }
  async deleteImg(filename) {
    return await webApi.delete(`/car-detail/image?id=${filename}`, {
      disableToast: true,
    });
  }
  async rotate(img) {
    const { data } = await webApi.patch(
      `car-detail/image/rotate?id=${img.filename}`,
      null,
      {
        disableToast: true,
      }
    );
    return data;
  }
  async detectPredominantColors(formData) {
    try {
      const { data } = await webApi.post('/car-detail/image-detecting/predominant', formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        disableToast: true
      });
      return data;
    } catch (error) {
      console.log(error);
      return false;
    }
  }
}

const photoUploadService = new PhotoUploadService()

export default photoUploadService
